import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import UpdatePaymentMethodForm from './UpdatePaymentMethodForm';
import TokenIcon from './TokenIcon';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const initials = (nameOrEmail) =>
  (nameOrEmail || '?')
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const ProfilePage = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [searchParams] = useSearchParams();
  const [checkoutMessage, setCheckoutMessage] = useState('');

  // Payment method + subscriptions state
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isPaymentMethodLoading, setIsPaymentMethodLoading] = useState(true);
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(true);
  const [isCanceling, setIsCanceling] = useState(null);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  useEffect(() => {
    const checkoutReturn = searchParams.get('checkout_return');
    if (checkoutReturn === 'true') {
      setCheckoutMessage('Your purchase was successful! Your balance and history will update shortly.');
      window.history.replaceState(null, '', window.location.pathname);
    }

    const fetchData = async () => {
      if (!user) return;

      setIsLoading(true);
      try {
        const response = await api.get('/users/me/transactions', { params: { filter } });
        setTransactions(response.data);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        setError('Could not load transaction history.');
      } finally {
        setIsLoading(false);
      }

      setIsPaymentMethodLoading(true);
      try {
        const response = await api.get('/users/me/payment-method');
        setPaymentMethod(response.data);
      } catch (err) {
        console.error('Failed to fetch payment method:', err);
        setPaymentMethod(null);
      } finally {
        setIsPaymentMethodLoading(false);
      }

      setIsSubscriptionsLoading(true);
      try {
        const response = await api.get('/subscriptions');
        setSubscriptions(response.data);
      } catch (err) {
        console.error('Failed to fetch subscriptions:', err);
        setError((prev) => (prev ? `${prev} Could not load subscriptions.` : 'Could not load subscriptions.'));
      } finally {
        setIsSubscriptionsLoading(false);
      }
    };

    fetchData();
  }, [user, filter, searchParams]);

  if (isAuthLoading) {
    return <div className="text-center text-wp-muted p-12">Loading…</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = typeof dateString === 'number' ? new Date(dateString * 1000) : new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatCurrency = (amount, currency) => {
    if (amount === null || amount === undefined || !currency) {
      return amount === null ? 'N/A' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(0);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  };

  const handleToggleRow = (txId) => setExpandedRowId(expandedRowId === txId ? null : txId);

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the current billing period.')) {
      return;
    }
    setIsCanceling(subscriptionId);
    setError(null);
    try {
      await api.post(`/subscriptions/${subscriptionId}/cancel`);
      const response = await api.get('/subscriptions');
      setSubscriptions(response.data);
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError(err.response?.data?.message || 'Could not cancel the subscription. Please try again later.');
    } finally {
      setIsCanceling(null);
    }
  };

  const handleUpdatePaymentClick = async () => {
    setIsUpdateLoading(true);
    setError(null);
    try {
      const response = await api.post('/users/me/setup-intent');
      setSetupIntentClientSecret(response.data.clientSecret);
      setIsUpdatingPayment(true);
    } catch (err) {
      console.error('Failed to create setup intent:', err);
      setError(err.response?.data?.message || 'Could not start payment method update. Please try again.');
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleUpdateCancel = () => {
    setIsUpdatingPayment(false);
    setSetupIntentClientSecret(null);
  };

  const handleUpdateSuccess = () => {
    setIsUpdatingPayment(false);
    setSetupIntentClientSecret(null);
    setIsPaymentMethodLoading(true);
    api.get('/users/me/payment-method')
      .then((response) => setPaymentMethod(response.data))
      .catch((err) => console.error('Failed to refetch payment method:', err))
      .finally(() => setIsPaymentMethodLoading(false));
  };

  const getFilterButtonClass = (buttonFilter) =>
    `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
      filter === buttonFilter ? 'bg-wp-accent text-wp-bg font-bold' : 'bg-wp-surface text-wp-muted hover:text-white'
    }`;

  const hasActiveSub = subscriptions.length > 0;
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  return (
    user && (
      <div className="max-w-3xl mx-auto space-y-6">
        {checkoutMessage && (
          <div className="p-4 rounded-xl bg-wp-surface border border-wp-success text-wp-success text-center text-sm">
            {checkoutMessage}
          </div>
        )}

        {/* User card */}
        <div className="bg-wp-surface rounded-2xl p-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-wp-accent flex items-center justify-center shrink-0">
            <span className="text-wp-bg text-xl font-bold">{initials(displayName)}</span>
          </div>
          <div className="min-w-0">
            <h2 className="text-white text-lg font-bold truncate">{displayName}</h2>
            <p className="text-wp-muted text-sm truncate">{user.email}</p>
            <span className={`inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide border border-wp-accent ${hasActiveSub ? 'bg-wp-accent text-wp-bg' : 'text-white'}`}>
              {hasActiveSub ? '👑 WALLPASS PRO' : <><TokenIcon size={11} /> FREE PLAN</>}
            </span>
          </div>
          <div className="ml-auto text-right shrink-0">
            <p className="text-wp-muted text-[11px] tracking-[2px]">BALANCE</p>
            <p className="flex items-center justify-end gap-2 text-wp-accent text-3xl font-bold">
              <TokenIcon size={30} flat={false} /> {user.wallpassTokenBalance.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Subscriptions */}
        <div>
          <h3 className="text-wp-muted text-[11px] tracking-[3px] mb-2">SUBSCRIPTION & BILLING</h3>
          <div className="bg-wp-surface rounded-2xl p-5">
            <h4 className="text-white font-semibold mb-3">Your Subscriptions</h4>
            {isSubscriptionsLoading ? (
              <p className="text-wp-muted text-sm">Loading subscriptions…</p>
            ) : subscriptions.length > 0 ? (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="bg-wp-bg border border-wp-border p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <p className="text-white font-medium">👑 {sub.productName}</p>
                      <p className="text-wp-muted text-sm">
                        {formatCurrency(sub.amount, sub.currency)} / {sub.interval}
                      </p>
                      {sub.cancel_at_period_end ? (
                        <p className="text-sm text-wp-accent font-semibold">Cancels on {formatDate(sub.current_period_end)}</p>
                      ) : (
                        <p className="text-sm text-wp-success">Renews on {formatDate(sub.current_period_end)}</p>
                      )}
                    </div>
                    {sub.status === 'active' && !sub.cancel_at_period_end && (
                      <button
                        onClick={() => handleCancelSubscription(sub.id)}
                        disabled={isCanceling === sub.id}
                        className="px-4 py-2 text-sm font-semibold rounded-xl text-wp-danger border border-wp-danger hover:bg-wp-surface transition-colors disabled:opacity-50 w-full sm:w-auto"
                      >
                        {isCanceling === sub.id ? 'Canceling…' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-wp-muted text-sm">You do not have any active subscriptions.</p>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-wp-surface rounded-2xl p-5">
          <h4 className="text-white font-semibold mb-3">Default Payment Method</h4>
          {isUpdatingPayment && setupIntentClientSecret ? (
            <div className="rounded-xl overflow-hidden bg-white p-4">
              <Elements stripe={stripePromise} options={{ clientSecret: setupIntentClientSecret }}>
                <UpdatePaymentMethodForm onCancel={handleUpdateCancel} onSuccess={handleUpdateSuccess} />
              </Elements>
            </div>
          ) : isPaymentMethodLoading ? (
            <p className="text-wp-muted text-sm">Loading payment details…</p>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {paymentMethod ? (
                <div>
                  <p className="text-white font-semibold capitalize">
                    💳 {paymentMethod.brand} •••• {paymentMethod.last4}
                  </p>
                  <p className="text-wp-muted text-sm">
                    Expires {String(paymentMethod.exp_month).padStart(2, '0')}/{paymentMethod.exp_year}
                  </p>
                </div>
              ) : (
                <p className="text-wp-muted text-sm">You do not have a default payment method on file.</p>
              )}
              <button
                onClick={handleUpdatePaymentClick}
                disabled={isUpdateLoading}
                className="px-4 py-2 text-sm font-bold rounded-xl text-wp-bg bg-wp-accent hover:opacity-80 transition-opacity disabled:opacity-50 w-full sm:w-auto"
              >
                {isUpdateLoading ? 'Loading…' : paymentMethod ? 'Update Card' : 'Add Card'}
              </button>
            </div>
          )}
        </div>

        {/* Transaction History */}
        <div className="bg-wp-surface rounded-2xl p-5">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h4 className="text-white font-semibold">Transaction History</h4>
            <div className="flex space-x-2">
              <button onClick={() => setFilter('all')} className={getFilterButtonClass('all')}>All</button>
              <button onClick={() => setFilter('additions')} className={getFilterButtonClass('additions')}>Additions</button>
              <button onClick={() => setFilter('deductions')} className={getFilterButtonClass('deductions')}>Deductions</button>
            </div>
          </div>
          {isLoading ? (
            <p className="text-center text-wp-muted py-4">Loading history…</p>
          ) : error ? (
            <p className="text-center text-wp-danger py-4">{error}</p>
          ) : transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-wp-border">
                <thead>
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-wp-muted uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-wp-muted uppercase tracking-wider">Description</th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-wp-muted uppercase tracking-wider">Tokens</th>
                    <th scope="col" className="relative px-4 py-3">
                      <span className="sr-only">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-wp-border">
                  {transactions.map((tx) => {
                    const isExpanded = expandedRowId === tx.id;
                    const isPurchase = tx.stripePayment && (tx.transactionType === 'purchase' || tx.transactionType === 'subscription_renewal');

                    return (
                      <React.Fragment key={tx.id}>
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-wp-muted">{formatDate(tx.createdAt)}</td>
                          <td className="px-4 py-3 text-sm text-white max-w-[280px] truncate">{tx.contentUrl}</td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-bold ${tx.tokensChanged > 0 ? 'text-wp-success' : 'text-wp-danger'}`}>
                            {tx.tokensChanged > 0 ? '+' : ''}{tx.tokensChanged.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            {isPurchase && (
                              <button onClick={() => handleToggleRow(tx.id)} className="text-wp-accent hover:opacity-80 font-semibold">
                                Details <span className="inline-block transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && isPurchase && (
                          <tr className="bg-wp-bg">
                            <td colSpan="4" className="px-4 py-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="font-semibold text-wp-muted">Price</p>
                                  <p className="text-white">{formatCurrency(tx.stripePayment.amount - (tx.stripePayment.tax || 0), tx.stripePayment.currency)}</p>
                                </div>
                                <div>
                                  <p className="font-semibold text-wp-muted">Tax</p>
                                  <p className="text-white">{formatCurrency(tx.stripePayment.tax, tx.stripePayment.currency)}</p>
                                </div>
                                <div>
                                  <p className="font-semibold text-wp-muted">Total</p>
                                  <p className="font-bold text-white">{formatCurrency(tx.stripePayment.amount, tx.stripePayment.currency)}</p>
                                </div>
                                <div>
                                  <a href={tx.stripePayment.receiptUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-wp-accent hover:underline">View Receipt &rarr;</a>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-wp-muted py-4">You have no transaction history.</p>
          )}
        </div>
      </div>
    )
  );
};

export default ProfilePage;
